/**
 * ExplorationZoneFormDialog — Sheet Migration Wave 4b (AC-W4b-03)
 *
 * CREATE-only FormSheet (right-slide, md:max-w-3xl). Exploration Zones are
 * immutable — only their status transitions (active → expired by scheduler,
 * active → revoked by user).
 *
 * 7 inputs per AC-W4b-03:
 *   1. title              (Input, required, 3-255 chars)
 *   2. description        (Textarea, required)
 *   3. domain             (Select, required)
 *   4. scopeText          (Textarea, JSON object as text)
 *   5. excludedCategoriesText (Input, comma-separated)
 *   6. expiresAt          (DateInput, required, must be future)
 *   7. decisionNoteId     (Input, UUID, required)
 *
 * Prior implementation used Modal — replaced with FormSheet + useFormSnapshot
 * for dirty-state guard, discard banner, and sticky header (W2.5 hooks).
 *
 * URL routing: opt-in via urlKey/urlId props — wired on ExplorationZonesPage.
 *
 * GOV-01 — Decision Note must exist BEFORE the zone (the schema enforces it).
 */

import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { FormSheet } from '@/components/ui/FormSheet';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { DateInput } from '@/components/ui/DateInput';
import { Textarea } from '@/components/ui/Textarea';
import { showToast } from '@/components/ui/Toast';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { useRorosExplorationZoneCreate } from '@/hooks/roros/governance';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type ExplorationZoneSnapshot = {
  title: string;
  description: string;
  domainId: string;
  scopeText: string;
  excludedCategoriesText: string;
  expiresAt: string;
  decisionNoteId: string;
};

function diffExplorationZoneSnapshots(
  base: ExplorationZoneSnapshot,
  cur: ExplorationZoneSnapshot,
): Partial<Record<keyof ExplorationZoneSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    domainId: base.domainId !== cur.domainId,
    scopeText: base.scopeText !== cur.scopeText,
    excludedCategoriesText: base.excludedCategoriesText !== cur.excludedCategoriesText,
    expiresAt: base.expiresAt !== cur.expiresAt,
    decisionNoteId: base.decisionNoteId !== cur.decisionNoteId,
  };
}

// ─── Field error type ─────────────────────────────────────────────────────────

interface FieldErrors {
  title?: string;
  description?: string;
  domainId?: string;
  scopeText?: string;
  expiresAt?: string;
  decisionNoteId?: string;
  general?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert a `YYYY-MM-DD` from DateInput into a Date representing
 * 23:59:59.999 in the viewer's local timezone.
 *
 * Canonical-timezone choice for this form: viewer-local (the wall clock the
 * user picks against in the DateInput). This avoids the prior bug where
 * a PST user picking "today" sent 23:59 UTC → already in the past → server 422.
 */
function _localEndOfDay(yyyyMmDd: string): Date | null {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  /** URL routing: when set, FormSheet syncs ?urlKey=urlId in the URL. */
  urlKey?: string;
  urlId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ExplorationZoneFormDialog({ isOpen, onClose, onCreated, urlKey, urlId }: Props) {
  const createMutation = useRorosExplorationZoneCreate();
  const domainsQuery = useRorosDomains();
  const activeDomains = ((domainsQuery.data as RorosDomainDTO[] | undefined) ?? []).filter(
    (d) => d.isActive,
  );

  const [errors, setErrors] = useState<FieldErrors>({});

  // ─── Form state via useFormSnapshot ────────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<ExplorationZoneSnapshot>({
    initialFactory: () => ({
      title: '',
      description: '',
      domainId: '',
      scopeText: '{}',
      excludedCategoriesText: '',
      expiresAt: '',
      decisionNoteId: '',
    }),
    isOpen,
    depKey: null,
  });

  // Reset errors when sheet opens.
  useEffect(() => {
    if (isOpen) setErrors({});
  }, [isOpen]);

  const {
    title,
    description,
    domainId,
    scopeText,
    excludedCategoriesText,
    expiresAt,
    decisionNoteId,
  } = state;

  // ─── Validation ────────────────────────────────────────────────────────────

  const validate = (): { ok: boolean; errs: FieldErrors; payload?: Record<string, unknown> } => {
    const e: FieldErrors = {};

    const trimmedTitle = title.trim();
    if (trimmedTitle.length < 3) e.title = t('Title must be at least 3 characters');
    if (trimmedTitle.length > 255) e.title = t('Title must be 255 characters or fewer');

    if (!description.trim()) e.description = t('Description is required');
    if (!domainId) e.domainId = t('Domain is required');

    let scope: Record<string, unknown> = {};
    try {
      const parsed = scopeText.trim() === '' ? {} : JSON.parse(scopeText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        e.scopeText = t('Scope must be a JSON object (e.g. {})');
      } else {
        scope = parsed as Record<string, unknown>;
      }
    } catch {
      e.scopeText = t('Scope must be valid JSON');
    }

    const excludedCategories = excludedCategoriesText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (!expiresAt) {
      e.expiresAt = t('Expiry date is required');
    } else {
      const exp = _localEndOfDay(expiresAt);
      if (!exp || Number.isNaN(exp.getTime())) {
        e.expiresAt = t('Invalid date');
      } else if (exp.getTime() <= Date.now()) {
        e.expiresAt = t('Expiry date must be in the future');
      }
    }

    const trimmedDecisionNoteId = decisionNoteId.trim();
    if (!trimmedDecisionNoteId) {
      e.decisionNoteId = t('Decision Note ID is required');
    } else if (
      !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        trimmedDecisionNoteId,
      )
    ) {
      e.decisionNoteId = t('Decision Note ID must be a valid UUID');
    }

    if (Object.keys(e).length > 0) {
      return { ok: false, errs: e };
    }

    return {
      ok: true,
      errs: {},
      payload: {
        title: trimmedTitle,
        description: description.trim(),
        domainId,
        scope,
        excluded_categories: excludedCategories,
        expires_at: _localEndOfDay(expiresAt)!.toISOString(),
        decision_note_id: trimmedDecisionNoteId,
      },
    };
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const v = validate();
    if (!v.ok) {
      setErrors(v.errs);
      return;
    }
    setErrors({});
    createMutation.mutate(v.payload!, {
      onSuccess: () => {
        showToast.success(t('Exploration zone created'));
        onCreated?.();
        onClose();
      },
      onError: (err: Error) => {
        const message = err?.message ?? t('Failed to create exploration zone');
        if (message.toLowerCase().includes('decision note')) {
          setErrors({ decisionNoteId: message });
        } else if (message.toLowerCase().includes('domain')) {
          setErrors({ domainId: message });
        } else if (message.toLowerCase().includes('expires')) {
          setErrors({ expiresAt: message });
        } else {
          setErrors({ general: message });
        }
      },
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <FormSheet<ExplorationZoneSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={t('New Exploration Zone')}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffExplorationZoneSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={createMutation.isPending}
      submitLabel={t('Create Exploration Zone')}
      submittingLabel={t('Creating…')}
      submitTestId="exploration-zone-submit"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      <div className="space-y-5 pt-2" data-testid="exploration-zone-form">
        {errors.general && (
          <Alert variant="error" title={t('Error')}>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
              <span>{errors.general}</span>
            </div>
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Title')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Input
            type="text"
            value={title}
            onChange={(e) => {
              setField('title', e.target.value);
              if (errors.title) setErrors((p) => ({ ...p, title: undefined }));
            }}
            error={errors.title}
            placeholder={t('e.g. "AI Operator pilot — Sales onboarding"')}
            maxLength={255}
            required
            autoFocus
            data-testid="exploration-zone-title"
          />
        </div>

        {/* 2. Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Description')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => {
              setField('description', e.target.value);
              if (errors.description) setErrors((p) => ({ ...p, description: undefined }));
            }}
            placeholder={t('Describe the exploration intent + exit criteria.')}
            data-testid="exploration-zone-description"
          />
          {errors.description && (
            <p role="alert" className="mt-1 text-xs text-error-600">{errors.description}</p>
          )}
        </div>

        {/* 3. Domain */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Domain')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <div data-testid="exploration-zone-domain">
            <Select
              value={domainId}
              onValueChange={(value) => {
                setField('domainId', value);
                if (errors.domainId) setErrors((p) => ({ ...p, domainId: undefined }));
              }}
              error={errors.domainId}
              placeholder={t('Select a domain…')}
              options={activeDomains.map((d) => ({ value: d.id, label: d.name }))}
            />
          </div>
        </div>

        {/* 4. Scope (JSON) */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Scope (JSON)')}
          </label>
          <Textarea
            rows={3}
            value={scopeText}
            onChange={(e) => {
              setField('scopeText', e.target.value);
              if (errors.scopeText) setErrors((p) => ({ ...p, scopeText: undefined }));
            }}
            placeholder={'{"actors": ["operator-1"], "limits": {"budget_usd": 50}}'}
            data-testid="exploration-zone-scope"
            className="font-mono text-xs"
          />
          {errors.scopeText && (
            <p role="alert" className="mt-1 text-xs text-error-600">{errors.scopeText}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Polymorphic scope spec for what the zone covers. Defaults to an empty object.')}
          </p>
        </div>

        {/* 5. Excluded categories */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Excluded categories')}
          </label>
          <Input
            type="text"
            value={excludedCategoriesText}
            onChange={(e) => setField('excludedCategoriesText', e.target.value)}
            placeholder={t('commit, decision, release')}
            data-testid="exploration-zone-excluded-categories"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Comma-separated list of action categories that the zone does NOT relax (e.g. commit, decision, release).')}
          </p>
        </div>

        {/* 6. Expires at */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Expires at')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <div data-testid="exploration-zone-expires-at">
            <DateInput
              value={expiresAt}
              onChange={(isoDate) => {
                setField('expiresAt', isoDate);
                if (errors.expiresAt) setErrors((p) => ({ ...p, expiresAt: undefined }));
              }}
              error={errors.expiresAt}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Zones force a deadline so exploration does not drift indefinitely.')}
          </p>
        </div>

        {/* 7. Decision Note ID */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Decision Note ID')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Input
            type="text"
            value={decisionNoteId}
            onChange={(e) => {
              setField('decisionNoteId', e.target.value);
              if (errors.decisionNoteId) setErrors((p) => ({ ...p, decisionNoteId: undefined }));
            }}
            error={errors.decisionNoteId}
            placeholder="00000000-0000-0000-0000-000000000000"
            data-testid="exploration-zone-decision-note-id"
            className="font-mono text-xs"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('UUID of the approved Decision Note that authorized this exploration zone (GOV-01).')}
          </p>
        </div>
      </div>
    </FormSheet>
  );
}
